import React, { useState, useEffect } from 'react';
import {
  FiDollarSign, FiCalendar, FiTag, FiFileText, FiCheck, FiX,
  FiUploadCloud, FiClock, FiCreditCard, FiSmartphone, FiRepeat
} from 'react-icons/fi';
import Modal from '../../components/shared/Modal';

const ExpenseFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  categories = [],
  loading = false,
}) => {
  const isEdit = !!initialData?.id;

  const [formData, setFormData] = useState({
    title: '',
    category_code: 'rent',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_status: 'paid',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    beneficiary: '',
    beneficiary_phone: '',
    receipt_reference: '',
    description: '',
    is_recurring: false,
    recurring_frequency: 'monthly',
  });

  const [receiptFile, setReceiptFile] = useState(null);
  const [existingReceiptUrl, setExistingReceiptUrl] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        category_code: initialData.category_code || 'other',
        amount: initialData.amount !== undefined ? String(initialData.amount) : '',
        expense_date: initialData.expense_date ? String(initialData.expense_date).split('T')[0] : new Date().toISOString().split('T')[0],
        payment_status: initialData.payment_status || 'paid',
        payment_method: initialData.payment_method || 'cash',
        payment_date: initialData.payment_date ? String(initialData.payment_date).split('T')[0] : '',
        beneficiary: initialData.beneficiary || '',
        beneficiary_phone: initialData.beneficiary_phone || '',
        receipt_reference: initialData.receipt_reference || '',
        description: initialData.description || '',
        is_recurring: !!initialData.is_recurring,
        recurring_frequency: initialData.recurring_frequency || 'monthly',
      });
      setExistingReceiptUrl(initialData.receipt_url || null);
      setReceiptFile(null);
    } else {
      setFormData({
        title: '',
        category_code: 'rent',
        amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        payment_status: 'paid',
        payment_method: 'cash',
        payment_date: new Date().toISOString().split('T')[0],
        beneficiary: '',
        beneficiary_phone: '',
        receipt_reference: '',
        description: '',
        is_recurring: false,
        recurring_frequency: 'monthly',
      });
      setExistingReceiptUrl(null);
      setReceiptFile(null);
    }
  }, [initialData, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setReceiptFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('Veuillez renseigner le titre de la dépense.');
      return;
    }

    const numAmount = parseFloat(formData.amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      alert('Veuillez renseigner un montant valide supérieur à 0.');
      return;
    }

    // Création FormData pour gérer le fichier uploadé
    const submitData = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== undefined) {
        submitData.append(key, formData[key]);
      }
    });

    if (receiptFile) {
      submitData.append('file', receiptFile);
    }

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? '✏️ Modifier la Dépense' : '➕ Enregistrer une Dépense d\'Agence'}
      maxWidth="720px"
    >
      <form onSubmit={handleSubmit} style={{ fontSize: 13 }}>
        {/* Titre & Catégorie */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div className="form-group">
            <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
              Libellé / Titre de la dépense *
            </label>
            <input
              type="text"
              name="title"
              className="form-control"
              placeholder="Ex: Facture EDM Février 2026, Loyer siège ACI 2000, Bon essence..."
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
              Catégorie de Dépense *
            </label>
            <select
              name="category_code"
              className="form-control"
              value={formData.category_code}
              onChange={handleChange}
              required
            >
              {categories.map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Montant & Date de dépense */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div className="form-group">
            <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
              Montant (FCFA / XOF) *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                name="amount"
                className="form-control"
                placeholder="Ex: 50000"
                min="1"
                step="1"
                value={formData.amount}
                onChange={handleChange}
                required
                style={{ paddingRight: 45, fontWeight: 700, fontSize: 15 }}
              />
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: 11, fontWeight: 700 }}>
                XOF
              </span>
            </div>
          </div>

          <div className="form-group">
            <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
              Date de la dépense *
            </label>
            <input
              type="date"
              name="expense_date"
              className="form-control"
              value={formData.expense_date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
              Statut du Paiement *
            </label>
            <select
              name="payment_status"
              className="form-control"
              value={formData.payment_status}
              onChange={handleChange}
              style={{
                fontWeight: 700,
                color: formData.payment_status === 'paid' ? '#15803d' : (formData.payment_status === 'pending' ? '#b45309' : '#4338ca')
              }}
            >
              <option value="paid">✅ Payé (Effectué)</option>
              <option value="pending">⏳ En attente de paiement</option>
              <option value="planned">📅 Planifié (Échéance future)</option>
            </select>
          </div>
        </div>

        {/* Règlement & Bénéficiaire */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div className="form-group">
            <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
              Mode de Règlement
            </label>
            <select
              name="payment_method"
              className="form-control"
              value={formData.payment_method}
              onChange={handleChange}
            >
              <option value="cash">💵 Espèces / Caisse Agence</option>
              <option value="orange_money">📱 Orange Money Mali</option>
              <option value="moov_money">📱 Moov Money Mali</option>
              <option value="wave">🌊 Wave Mali</option>
              <option value="bank_transfer">🏦 Virement Bancaire</option>
              <option value="check">📜 Chèque Bancaire</option>
              <option value="card">💳 Carte Bancaire</option>
              <option value="other">Autre moyen</option>
            </select>
          </div>

          {formData.payment_status === 'paid' && (
            <div className="form-group">
              <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Date de Paiement Effectif
              </label>
              <input
                type="date"
                name="payment_date"
                className="form-control"
                value={formData.payment_date}
                onChange={handleChange}
              />
            </div>
          )}

          <div className="form-group">
            <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
              Bénéficiaire / Fournisseur
            </label>
            <input
              type="text"
              name="beneficiary"
              className="form-control"
              placeholder="Ex: EDM SA, Station Total, Bailleur..."
              value={formData.beneficiary}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Justificatif & Référence */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div className="form-group">
            <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
              N° Reçu / Facture / Bon
            </label>
            <input
              type="text"
              name="receipt_reference"
              className="form-control"
              placeholder="Ex: FACT-EDM-2026-981, BON-ESS-45..."
              value={formData.receipt_reference}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
              Téléphone Bénéficiaire (optionnel)
            </label>
            <input
              type="text"
              name="beneficiary_phone"
              className="form-control"
              placeholder="Ex: +223 70 12 34 56"
              value={formData.beneficiary_phone}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Upload Pièce jointe / Reçu */}
        <div className="form-group" style={{ marginBottom: 12, background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px dashed #cbd5e1' }}>
          <label style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiUploadCloud /> Justificatif / Facture / Reçu de caisse (PDF ou Image)
          </label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileChange}
            className="form-control"
            style={{ fontSize: 12 }}
          />
          {receiptFile && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#15803d', fontWeight: 600 }}>
              📎 Fichier sélectionné : {receiptFile.name} ({(receiptFile.size / 1024).toFixed(1)} Ko)
            </div>
          )}
          {!receiptFile && existingReceiptUrl && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#2563eb' }}>
              📎 Justificatif existant déjà enregistré (laissez vide pour conserver).
            </div>
          )}
        </div>

        {/* Dépense récurrente */}
        <div style={{ marginBottom: 12, background: '#faf5ff', padding: 10, borderRadius: 8, border: '1px solid #e9d5ff' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, margin: 0 }}>
            <input
              type="checkbox"
              name="is_recurring"
              checked={formData.is_recurring}
              onChange={handleChange}
              style={{ width: 16, height: 16 }}
            />
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiRepeat color="#7c3aed" /> Dépense récurrente (Loyer mensuel, fibre internet, salaire...)
            </span>
          </label>
          {formData.is_recurring && (
            <div style={{ marginTop: 8, paddingLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#6b21a8' }}>Fréquence :</span>
              <select
                name="recurring_frequency"
                className="form-control"
                value={formData.recurring_frequency}
                onChange={handleChange}
                style={{ width: 'auto', display: 'inline-block', fontSize: 12, padding: '3px 8px' }}
              >
                <option value="monthly">Mensuelle (Chaque mois)</option>
                <option value="quarterly">Trimestrielle (Tous les 3 mois)</option>
                <option value="yearly">Annuelle (Chaque année)</option>
              </select>
            </div>
          )}
        </div>

        {/* Description / Notes */}
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
            Description & Notes complémentaires
          </label>
          <textarea
            name="description"
            rows={2}
            className="form-control"
            placeholder="Détails sur l'opération, motif du bon d'essence, numéro de compteur EDM, etc."
            value={formData.description}
            onChange={handleChange}
          />
        </div>

        {/* Boutons d'action */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Annuler
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 160, justifyContent: 'center' }}
          >
            {loading ? (
              <span>Enregistrement...</span>
            ) : (
              <>
                <FiCheck size={16} />
                <span>{isEdit ? 'Mettre à jour' : 'Enregistrer la Dépense'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ExpenseFormModal;
